import React, { useState, useEffect, Fragment, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRealtimeUnits, useRealtimeDrivers } from 'Core/hooks';
import { useIntl } from "react-intl";
import moment from "moment";

/** Global components */
import { Grid, Chip, Box, Divider, Icon } from "@mui/material";
import { Button, Form, Select, Input, ToggleSwitch, LinearProgress, Slider, Pill } from "Components";
import { NotificationManager } from "Components/Notifications";
import VehicleStatusIconPicker from "../vehicleStatusIconPicker"
import Tabs, { TabContainer } from 'Components/Tabs';
import Modal, { ModalBody, ModalFooter } from "Components/Modal";
import { GetApiErrorLan } from 'Core/logManager'
/** Helpers */
import { formatCatalogDate, getValueFloat, GetFormatForMoment, getFormattedDateByTimeZone } from "Core/data/Helpers";
import { HasPermission } from "Core/security"
import {
  openTroubleshootDeviceModal,
  unitEditUnitClose,
  catalogLoad,
  unitEditUpdate,
  catalogProcessItem,
  catalogProcessItems,
  decodeVin,
  clearRedux
} from "Redux/actions";

import './styles.scss';
import { convertToKPH, convertToMI } from "../../../../../../core/data/Helpers";
import { IconButton, Tooltip, Icons } from "@gpstrackit/gtc-react-controls";
import TagsManage from "Modules/.common/components/TagsManage";
import ModalTroubleshootDevice from 'Modules/.common/components/TroubleshootDevice';

const EditUnitSettings = (props) => {
  const frmCompleteService = useRef();
  const dispatch = useDispatch();
  const { messages, locale } = useIntl();
  const { isOpen, loading, unit, resultUpdate, asset, vehicleInformationLoading } = useSelector(state => state.editUnitRedux);

  const drivers = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });
  const locations = useSelector(state => state.catalogsRedux.locations);
  const timeZones = useSelector(state => state.catalogsRedux.timeZones);
  const inputLabels = useSelector(state => state.catalogsRedux.inputLabels);
  const vehicleStatus = useSelector(state => state.catalogsRedux.vehicleStatus);
  const openModalTroubleshootDevice = useSelector(state => state.troubleshootDeviceRedux.open);

  const user = useSelector((state) => state.securityRedux.user);
  const contacts = useSelector(state => state.catalogsRedux.contacts);
  const units = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });

  const [unitId, setUnitId] = useState()
  const [formData, setFormData] = useState({});
  const [title, setTitle] = useState();
  const [supportedInputs, setSupportedInputs] = useState(0);
  const [supportedOutputs, setSupportedOutputs] = useState(0);
  const [inputsCounter, setInputsCounter] = useState([]);
  const [outputsCounter, setOutputsCounter] = useState([]);
  const [enableAll, setEnableAll] = useState(true);
  const [udpateVehicleStatusIcon, setUpdateVehicleStatusIcon] = useState(false);
  const [settingPillSelected, setSettingPillSelected] = useState(0);
  const [errorMessage, setErrorMessage] = useState();
  const [tab, setTab] = useState(0);

  const selectOutputOptions = [
    { type: "Toggle", label: messages["unit_toggle_options"] }
  ];

  if (unit?.type != "queclink") {
    selectOutputOptions.push({ type: "Pulse", label: messages["unit_pulse_options"] });
  }

  selectOutputOptions.push({ type: "singlePulse", label: messages["unit_pulse_single_option"] });

  /** get default values on units */
  const handleClose = useCallback(() => {
    dispatch(unitEditUnitClose())
    dispatch(clearRedux("MODAL_EDIT_UNIT"))
    setUnitId();
    setTitle();
    setFormData({});
    setUpdateVehicleStatusIcon(false);
    setSettingPillSelected(0);
    setErrorMessage();
    setTab(0);
  });

  /** handle checkbox change */
  const onEcuChange = (checked, key) => {
    setFormData({
      ...formData,
      [key]: Number(checked),
    });
  };

  const onSubmit = () => {
    const unitInputs = { c: [], u: [], d: [] };

    formData.inputs.map((item, index) => {
      if (item) {
        const _unit = unit.inputs.find((input) => input.labelId === item.labelId && input.position === item.position)

        if (_unit !== undefined) {
          unitInputs.u.push({
            unitId: formData.id,
            position: item.position,
            labelId: item.id || item.labelId,
          })
        } else {
          unitInputs.c.push({
            unitId: formData.id,
            position: item.position,
            labelId: item.id,
          })
        }
      }
    });

    const inputsToDelete = unit.inputs.filter(input => !formData.inputs.some(item => input.labelId === item.labelId && item.position === input.position));
    if (inputsToDelete.length > 0) {
      inputsToDelete.map(input => {
        unitInputs.d.push({ unitId: formData.id, position: input.position });
      });
    }

    const unitOutputs = { c: [], u: [] };
    // clean outputs incomplete
    const outputsFiltered = formData.outputs.filter(o => { return o?.name && o?.type })

    //** Create outputs individually */
    outputsFiltered.map((item, index) => {
      if (item) {
        if (
          unit.outputs.filter(
            (input) => input.name === item.name || input.position === index + 1
          ).length === 0
        ) {
          unitOutputs.c.push({
            unitId: formData.id,
            position: index + 1,
            name: item.name,
            type: item.type?.type || item.type || 'Toggle',
            state: "Off",
          });
        } else {
          unitOutputs.u.push({
            unitId: formData.id,
            position: index + 1,
            name: item.name,
            type: item.type?.type || item.type || 'Toggle',
            state: item.state || "Off",
          });
        }
      }
    });

    /*     if (!formData.tags?.length) {
          setTab(1);
          setErrorMessage(messages['unit_settings_tags_required']);
          return;
        } */

    let tagIds = [];
    tagIds = formData.tags.map((tag) => {
      return Number(tag.id);
    });

    let objectTosave = { ...formData, tagIds };
    let alertContacts = formData?.alertContacts?.map(alertContact => alertContact?.id) || [];
    objectTosave = {
      ...objectTosave,
      obd2alerts: unit?.obd2alerts,
      vbus: unit?.vbus,
      jbus: unit?.jbus,
      timeZone: timeZones.find(tz => tz.id === formData?.timeZoneId) || null,
      alertContacts: alertContacts,
      asset: {
        unitId: formData.id,
        vin: formData.vin,
        make: formData.make,
        model: formData.model,
        year: formData.year || null,
        engine: formData.engine,
        mpgHighway: formData.mpgHighway,
        mpgCity: formData.mpgCity,
        licensePlate: formData.licensePlate,
      },
    };

    if (!objectTosave.vehicleStatusId) {
      objectTosave.vehicleStatusId = 0;
    }

    let systemAlerts = [];
    let systemAlertsToDelete = [];

    let assignedTo = [{
      id: null,
      entity: {
        id: formData.id,
        label: "Unit",
        name: "Unit"
      }
    }];

    if (
      (formData?.driverBehaviorAlert?.rapidAcceleration ||
        formData?.driverBehaviorAlert?.hardBrake ||
        formData?.driverBehaviorAlert?.suddenStop ||
        formData?.driverBehaviorAlert?.hardTurn) && unit?.driverBehavior == 1 && unit?.unitVersionDriverBehavior == 1
    ) {
      let config = {
        "driverBehaviorEvents": []
      }

      if (formData?.driverBehaviorAlert?.rapidAcceleration) config?.driverBehaviorEvents?.push("RapidAcceleration");
      if (formData?.driverBehaviorAlert?.hardBrake) config?.driverBehaviorEvents?.push("HardStop");
      if (formData?.driverBehaviorAlert?.suddenStop) config?.driverBehaviorEvents?.push("SuddenStop");
      if (formData?.driverBehaviorAlert?.hardTurn) config?.driverBehaviorEvents?.push("HardTurn");

      systemAlerts.push({
        id: unit?.driverBehaviorAlert?.id || null,
        assignedTo: assignedTo,
        config: JSON.stringify(config),
        name: `DriverBehavior - ${unit?.label}`,
        severity: "Medium",
        subscribers: alertContacts.map(contactId => {
          return { alert: false, alertAfter: false, alertBefore: false, contactId: contactId, id: null }
        }),
        triggers: [],
        type: "DriverBehavior",
      });
    } else if (unit?.driverBehaviorAlert) {
      systemAlertsToDelete.push(unit?.driverBehaviorAlert);
    }

    if (formData?.towAlert?.tow && unit?.tow == 1) {
      systemAlerts.push({
        id: unit?.towAlert?.id || null,
        assignedTo: assignedTo,
        config: null,
        name: `Tow - ${unit?.label}`,
        severity: "Medium",
        subscribers: alertContacts.map(contactId => {
          return { alert: false, alertAfter: false, alertBefore: false, contactId: contactId, id: null }
        }),
        triggers: [],
        type: "Tow",
      });
    } else if (unit?.towAlert) {
      systemAlertsToDelete.push(unit?.towAlert);
    }

    if (formData?.motionAlert?.motion && unit?.motion == 1) {
      systemAlerts.push({
        id: unit?.motionAlert?.id || null,
        assignedTo: assignedTo,
        config: JSON.stringify({}),
        name: `Motion - ${unit?.label}`,
        severity: "Medium",
        subscribers: alertContacts.map(contactId => {
          return { alert: false, alertAfter: false, alertBefore: false, contactId: contactId, id: null }
        }),
        triggers: [],
        type: "Motion",
      });
    } else if (unit?.motionAlert) {
      systemAlertsToDelete.push(unit?.motionAlert);
    }

    if (formData?.consistentSpeedAlert?.consistentSpeed && unit.manufacturerId == 2) {
      systemAlerts.push({
        id: unit?.consistentSpeedAlert?.id || null,
        assignedTo: assignedTo,
        config: null,
        name: `Consistent Speed - ${unit?.label}`,
        severity: "Medium",
        subscribers: alertContacts.map(contactId => {
          return { alert: false, alertAfter: false, alertBefore: false, contactId: contactId, id: null }
        }),
        triggers: [],
        type: "ConsistentSpeed",
      });
    } else if (unit?.consistentSpeedAlert) {
      systemAlertsToDelete.push(unit?.consistentSpeedAlert);
    }

    // if (
    //   (systemAlerts?.length ||
    //     (unit?.obd2alerts == 1 && unit?.vbus == 1 || unit?.jbus == 1) ||
    //     formData.noGps ||
    //     formData.lowBattery) &&
    //   !alertContacts?.length
    // ) {
    //   setTab(2);
    //   setErrorMessage(messages['unit_settings_alert_contacts_required']);
    //   return;
    // }
    if (user.isKph) {
      objectTosave.offsetEngineOdometer = convertToMI(objectTosave.offsetEngineOdometer)
      objectTosave.offsetEcuEngineOdometer = convertToMI(objectTosave.offsetEcuEngineOdometer)
    }
    dispatch(unitEditUpdate({ ...objectTosave }, unitInputs, unitOutputs, systemAlerts, systemAlertsToDelete));
  };

  const settingsTab = {
    UNIT: 0,
    DATA: 1,
    VIN: 2,
    INPUT_LABELS: 3,
    OUTPUT_LABELS: 4,
    MPG: 5,
    NOTES: 6
  }

  const filterIcon = (unit) => {
    let vehicleStatusId = unit?.vehicleStatusId || unit.lastReading?.vehicleStatusId;
    return vehicleStatus?.find(x => x.id == vehicleStatusId)
  }

  const getTabs = useCallback(() => {
    const availableTabs = [
      {
        icon: "settings",
        label: messages["unit_settings_tab"],
        render: () => {
          const renderPillSelected = () => {
            switch (settingPillSelected) {
              case settingsTab.UNIT:
                return renderUnit();
              case settingsTab.DATA:
                return renderData();
              case settingsTab.VIN:
                return renderVin();
              case settingsTab.INPUT_LABELS:
                return renderInputLabels();
              case settingsTab.OUTPUT_LABELS:
                return renderOutputLabels();
              case settingsTab.MPG:
                return renderMpg();
              case settingsTab.NOTES:
                return renderNotes();
              default:
                return null;
            }
          }

          const renderUnit = () => {
            let icon;

            if (!_.isEmpty(formData)) {
              icon = filterIcon(formData);
            }

            return (
              <Fragment>
                {/* Unit Label */}
                <Box mt={3} mb={1}>
                  {/* Custom Vehicle status icon */}
                  <div className="box-toggle">
                    <label className="input-normal-label toggle-aligned-text">
                      {messages['unit_settings_label_and_icon']}
                    </label>
                    <ToggleSwitch
                      id="custom"
                      large
                      blueToggle
                      optionLabels={[messages['unit_settings_custom'], messages['unit_settings_custom']]}
                      checked={udpateVehicleStatusIcon}
                      onChange={(checked) => {
                        setFormData({
                          ...formData,
                          vehicleStatusId: checked ? filterIcon(unit)?.id : 0,
                        });
                        setUpdateVehicleStatusIcon(checked);
                      }}
                    />
                  </div>

                  {/* Vehicule status icon */}
                  {/* Unit label */}
                  <div style={{ display: 'flex', alignItems: 'end' }}>
                    <VehicleStatusIconPicker
                      icon={icon?.icon}
                      icons={vehicleStatus}
                      disabled={!udpateVehicleStatusIcon}
                      loading={loading}
                      onChange={(value) => {
                        setFormData({
                          ...formData,
                          vehicleStatusId: value?.id,
                        });
                      }}
                    />
                    <Input
                      id="label"
                      type="text"
                      placeholder={messages["unit_settings_unit"]}
                      required
                      requiredMessage={messages["required_field"]}
                      maxLength={100}
                      onChange={(value) =>
                        setFormData({ ...formData, label: value })
                      }
                      value={formData.label || ""}
                    />
                  </div>
                </Box>

                {/* License plate */}
                <Box mb={2}>
                  <div className="form-item" >
                    <Input
                      id="licensePlate"
                      type="text"
                      label={messages["unit_settings_licenseplate"]}
                      placeholder={messages["unit_settings_licenseplate"]}
                      required={false}
                      maxLength={20}
                      onChange={(value) =>
                        setFormData({ ...formData, licensePlate: value })
                      }
                      value={formData.licensePlate || ""}
                    />
                  </div>
                </Box>

                {/* Speed */}
                {
                  unit?.odometerEnable &&
                  <Box mb={1}>
                    <div className="box-toggle">
                      <label className="input-normal-label toggle-aligned-text">
                        {messages["unit_settings_speed"]}
                      </label>

                      {
                        (formData.jbus || formData.vbus) &&
                        <ToggleSwitch
                          id="speedEcu"
                          medium
                          blueToggle
                          optionLabels={["ECU", "ECU"]}
                          checked={Boolean(formData.useEcuSpeed) || false}
                          disabled={!formData.jbus && !formData.vbus}
                          onChange={(checked) =>
                            onEcuChange(checked, "useEcuSpeed")
                          }
                        />
                      }
                    </div>
                    <span className="subtitle">
                      {formData.useEcuSpeed
                        ? messages["unit_settings_calculated_by_ecu"]
                        : messages["unit_settings_calculated_by_gps"]}
                    </span>
                  </Box>
                }

                {/* Odometer */}
                {
                  unit?.odometerEnable &&
                  <Box mb={4}>
                    <div className="box-toggle">
                      <label className="input-normal-label toggle-aligned-text">
                        {messages["unit_settings_odometer"]}
                      </label>
                      {
                        (formData.jbus || formData.vbus) &&
                        <ToggleSwitch
                          id="odometerEcu"
                          medium
                          blueToggle
                          optionLabels={["ECU", "ECU"]}
                          checked={Boolean(formData.useEcuOdometer) || false}
                          disabled={!formData.jbus && !formData.vbus}
                          onChange={(checked) =>
                            onEcuChange(checked, "useEcuOdometer")
                          }
                        />
                      }
                    </div>
                    {/* <div>
                        <Input
                          id="odometer"
                          type="number"
                          label="ECU"
                          placeholder={
                            formData.useEcuOdometer
                              ? `${formData.ecuEngineOdometer || 0} (+ ${formData.offsetEcuEngineOdometer || 0
                              })`
                              : `${formData.engineOdometer || 0} (+ ${formData.offsetEngineOdometer || 0
                              })`
                          }
                          readOnly={true}
                          maxLength={12}
                          //value={ formData.useEcuOdometer ?  formData.ecuEngineOdometer  : formData.engineOdometer   }
                          onChange={() => null}
                        />
                      </div> */}
                    <Grid container>
                      <Grid xs={12} item>
                        <Input
                          id="odometerRead"
                          type="number"
                          decimalPlaces={2}
                          label={
                            `${messages["unit_settings_odometer_dash_reading"]} (${user.isKph ? "km" : "mi"})`
                          }
                          placeholder={
                            `${messages["unit_settings_odometer_dash_reading"]} (${user.isKph ? "km" : "mi"})`
                          }
                          maxLength={12}
                          max={100000000}
                          preventOnChange
                          onChange={(value) =>
                            setFormData({
                              ...formData,
                              dashOdometer: value,
                              offsetEcuEngineOdometer: Number(
                                value - formData.ecuEngineOdometer
                              ).toFixed(2),
                              offsetEngineOdometer: Number(
                                value - formData.engineOdometer
                              ).toFixed(2),
                              offsetEngineOdometerUpdatedOn:
                                !formData.useEcuOdometer
                                  ? moment().utc().format()
                                  : formData.offsetEcuEngineOdometerUpdatedOn ||
                                  null,
                              offsetEcuEngineOdometerUpdatedOn:
                                formData.useEcuOdometer
                                  ? moment().utc().format()
                                  : formData.offsetEcuEngineOdometerUpdatedOn ||
                                  null,
                            })
                          }
                          value={Number(formData.dashOdometer).toFixed(0)}
                        />
                      </Grid>
                      {/* <Grid xs={6} item>
                          <Input
                            id="odometerOffset"
                            type="number"
                            label={messages['unit_odometer_offset_label']}
                            value={formData?.useEcuOdometer && formData.offsetEcuEngineOdometer || formData.offsetEngineOdometer}
                            readOnly={ true }
                            onChange={ ()=> null }
                          />
                        </Grid> */}
                      <Grid sm={12} item>
                        {
                          formData.useEcuOdometer &&
                          <Box mt={0.5}>
                            <span className="subtitle">
                              {`ECU: ${formData.useEcuOdometer
                                ? `${formData.ecuEngineOdometer || 0} (+ ${formData.offsetEcuEngineOdometer || 0})`
                                : `${formData.engineOdometer || 0} (+ ${formData.offsetEngineOdometer || 0})
                                Offset: ${formData?.useEcuOdometer && formData.offsetEcuEngineOdometer || formData.offsetEngineOdometer}`}`}
                            </span>
                          </Box>
                        }
                        <Box mt={0.5}>
                          <span className="subtitle">
                            {formData?.offsetEngineOdometerUpdatedOnLabel &&
                              messages["unit_settings_last_updated"]
                              + " " +
                              formData?.offsetEngineOdometerUpdatedOnLabel}
                          </span>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                }

                {/* Engine Hours */}
                {
                  unit?.hoursEnable &&
                  <Fragment>
                    <Box mb={4}>
                      <div className="box-toggle">
                        <label className="input-normal-label toggle-aligned-text">
                          {messages["unit_settings_engine_hours"]}
                        </label>
                        {
                          (formData.jbus || formData.vbus) &&
                          <ToggleSwitch
                            id="engineHoursEcu"
                            medium
                            blueToggle
                            optionLabels={["ECU", "ECU"]}
                            checked={
                              Boolean(formData.useEcuEngineHours) || false
                            }
                            disabled={!formData.jbus && !formData.vbus}
                            onChange={(checked) =>
                              onEcuChange(checked, "useEcuEngineHours")
                            }
                          />
                        }
                      </div>

                      <Input
                        id="engineHoursRead"
                        type="number"
                        label={messages["unit_settings_actual"]}
                        placeholder={
                          messages[
                          "unit_settings_actual"
                          ]
                        }
                        decimalPlaces={2}
                        max={1000000}
                        preventOnChange
                        onChange={(value) =>
                          setFormData({
                            ...formData,
                            dashEngineHours: value,
                            offsetEcuEngineHours: Number(
                              value - formData.ecuEngineHours
                            ).toFixed(2),
                            offsetEngineHours: Number(
                              value - formData.engineHours
                            ).toFixed(2),
                            offsetEngineHoursUpdatedOn:
                              !formData.useEcuEngineHours
                                ? moment().utc().format()
                                : formData.offsetEngineHoursUpdatedOn ||
                                null,
                            offsetEcuEngineHoursUpdatedOn:
                              formData.useEcuEngineHours
                                ? moment().utc().format()
                                : formData.offsetEcuEngineHoursUpdatedOn ||
                                null,
                          })
                        }
                        value={formData.dashEngineHours}
                      />

                      <Grid sm={12} item>
                        {
                          formData.useEcuEngineHours != 0 &&
                          <Box mt={0.5}>
                            <span className="subtitle">
                              {`ECU: 
                                ${formData.useEcuEngineHours
                                  ? `${formData.ecuEngineHours || 0} (+ ${formData.offsetEcuEngineHours || 0
                                  })`
                                  : `${formData.engineHours || 0} (+ ${formData.offsetEngineHours || 0
                                  })`
                                }
                              `}
                            </span>
                          </Box>
                        }
                      </Grid>
                    </Box>
                  </Fragment>

                }

                {/* offsetEngineHoursUpdatedOnLabel */}
                {formData?.offsetEngineHoursUpdatedOnLabel &&
                  <div className="form-item">
                    <span className="subtitle">
                      {`${messages["unit_settings_last_updated"]} ${formData?.offsetEngineHoursUpdatedOnLabel}`}
                    </span>
                  </div>
                }

                {/* Driver */}
                <div className="form-item" >
                  <Select
                    isClearable={true}
                    id="driver"
                    label={messages["unit_settings_driver_assigned"]}
                    placeholder={messages["select"]}
                    options={drivers || []}
                    textField="name"
                    valueField="driverId"
                    value={
                      drivers
                        ? drivers.filter(
                          (driver) =>
                            driver?.driverId.toString() ===
                            formData.driverId?.toString()
                        )?.[0]
                        : {}
                    }
                    onChange={(value) => {
                      setFormData({
                        ...formData,
                        driverId: value?.driverId || 0,
                      });
                    }}
                  />
                </div>
                {
                  /* Default Driver with permision */
                  HasPermission(user, "defDrvr", [290])
                  &&
                  <div className="form-item" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '5px' }}>
                    <Select
                      isClearable={true}
                      id="driverDefault"
                      label={messages["unit_settings_driver_default"]}
                      placeholder={messages["select"]}
                      options={drivers || []}
                      textField="name"
                      valueField="driverId"
                      value={
                        drivers
                          ? drivers.filter(
                            (driver) =>
                              driver?.driverId.toString() ===
                              formData.defaultDriverId?.toString()
                          )?.[0]
                          : {}
                      }
                      onChange={(value) => {
                        setFormData({
                          ...formData,
                          defaultDriverId: value?.driverId || 0,
                        });
                      }}
                    />
                    <div style={{ paddingTop: 30 }}>
                      <Tooltip
                        placement="bottom"
                        title={messages['unit_settings_driver_default_info']}
                      >
                        <Icons
                          name="help"
                          size="medium"
                        />
                      </Tooltip>
                    </div>
                  </div>
                }
                {/* Location */}
                <Select
                  isClearable={true}
                  id="location"
                  label={messages["unit_settings_location"]}
                  placeholder={messages["select"]}
                  options={
                    locations?.filter(
                      (location) => location.active
                    ) || []
                  }
                  textField="title"
                  valueField="id"
                  value={
                    locations
                      ? locations.filter(
                        (location) =>
                          location?.id.toString() ===
                          formData.locationId?.toString()
                      )[0]
                      : {}
                  }
                  onChange={(value) => {
                    setFormData({
                      ...formData,
                      locationId: value?.id || 0,
                    });
                  }}
                />

                {/* Timezone */}
                <Select
                  id="timeZone"
                  label={messages["unit_settings_time_zone"]}
                  placeholder={messages["select"]}
                  options={timeZones || []}
                  textField="name"
                  valueField="id"
                  value={
                    (timeZones &&
                      timeZones.filter(
                        (timeZone) =>
                          timeZone.id.toString() ===
                          formData.timeZoneId?.toString()
                      )[0]) ||
                    false
                  }
                  onChange={(value) => {
                    setFormData({ ...formData, timeZoneId: value.id });
                  }}
                  required
                  requiredMessage={messages["required_field"]}
                />

                {/** If the manufacturer is Calamp (manufacturerId = 2) then show the consisted speed selector */}
                {
                  formData.manufacturerId === 2 &&
                  <div className="form-item" style={{ width: '100%' }}>
                    <span className="input-normal-label">
                      {messages["unit_settings_consistent_speed"]}
                    </span>
                    <Slider
                      min={0}
                      max={120}
                      sliderOneColor={false}
                      showValue
                      valueLabelDisplay={"off"}
                      measure="MPH"
                      value={formData.consistentSpeed || 0}
                      onChange={(event) =>
                        setFormData({ ...formData, consistentSpeed: event })
                      }
                    />
                  </div>
                }
              </Fragment>
            );
          }

          const renderData = () => {
            return (
              <div
                className="unitForm"
                style={{
                  width: "100%",
                  padding: '20px 0px'
                }}
              >
                <div className="box-toggle">
                  <label className="input-normal-label toggle-aligned-text">
                    ECU
                  </label>

                  <ToggleSwitch
                    id="ecuEnableDisableAll"
                    normal
                    blueToggle
                    optionLabels={[messages["unit_disable_all"], messages["unit_enable_all"]]}
                    checked={enableAll}
                    onChange={(checked) => {
                      setFormData({
                        ...formData,
                        useEcuEngineHours: checked,
                        useEcuOdometer: checked,
                        useEcuVin: checked,
                        useEcuSpeed: checked,
                      });
                      setEnableAll(checked);
                    }}
                  />
                </div>
              </div>
            )
          }

          const renderVin = () => {
            let currentVin = formData?.useEcuVin ? formData.ecuVin : formData.vin || "";

            return (
              <Fragment>
                <Box mt={3}>
                  <div style={{ width: '100%' }} className="box-toggle">
                    <label className="input-normal-label toggle-aligned-text">
                      VIN
                    </label>
                    {(formData.jbus || formData.vbus) && (
                      <ToggleSwitch
                        id="vinEcu"
                        medium
                        blueToggle
                        optionLabels={["ECU", "ECU"]}
                        checked={Boolean(formData.useEcuVin) || false}
                        onChange={(checked) => {
                          onEcuChange(checked, "useEcuVin");
                          if (checked && formData.ecuVin) {
                            dispatch(decodeVin({ vin: formData.ecuVin }));
                          }
                        }}
                      />
                    )}
                  </div>

                  <Input
                    id="vin"
                    type="text"
                    regex={/^[A-Za-z0-9]*$/}
                    maxLength={17}
                    minLength={17}
                    className="input-uppercase"
                    placeholder="VIN"
                    readOnly={Boolean(formData.useEcuVin)}
                    disabled={Boolean(formData.useEcuVin)}
                    value={
                      formData?.useEcuVin
                        ? formData.ecuVin
                        : formData.vin || ""
                    }
                    onChange={(value) =>
                      setFormData({ ...formData, vin: value })
                    }
                    exactLength={17}
                    exactLengthMessage={messages["maintenance_VINNumberValidation"]}
                    errorMessage={messages["maintenance_VINNumberValidation"]}
                    button={
                      <Button
                        className="btn-blue btn-xs-text"
                        disabled={!currentVin}
                        onClick={(e) => {
                          if (e) e.preventDefault();
                          if (currentVin) {
                            dispatch(decodeVin({ vin: currentVin }));
                          }
                        }}
                      >
                        {messages['unit_settings_decode_vin']}
                      </Button>
                    }
                  />
                </Box>

                <div className="unitForm" style={{ width: "100%" }}>
                  <Input
                    id="make"
                    type="text"
                    label={messages["unit_settings_make"]}
                    placeholder={messages["unit_settings_make"]}
                    maxLength={100}
                    onChange={(value) =>
                      setFormData({ ...formData, make: value })
                    }
                    value={formData.make || ""}
                  />
                </div>

                <div className="unitForm" style={{ width: "100%" }}>
                  <Input
                    id="model"
                    type="text"
                    label={messages["unit_settings_model"]}
                    placeholder={messages["unit_settings_model"]}
                    maxLength={100}
                    onChange={(value) =>
                      setFormData({ ...formData, model: value })
                    }
                    value={formData.model || ""}
                  />
                </div>

                <div className="unitForm" style={{ width: "100%" }}>
                  <Input
                    id="year"
                    type="number"
                    label={messages["unit_settings_year"]}
                    placeholder={messages["unit_settings_year"]}
                    decimalPlaces={0}
                    max={new Date().getFullYear() + 2}
                    min={formData.year ? 1950 : 0}
                    onChange={(value) =>
                      setFormData({ ...formData, year: value })
                    }
                    value={formData.year}
                    maxLength={4}
                    errorMessage={
                      messages["unit_settings_date_validation_message"] +
                      " " +
                      String(new Date().getFullYear() + 2)
                    }
                  />
                </div>

                <div className="unitForm" style={{ width: "100%" }}>
                  <Input
                    id="engine_liters"
                    type="number"
                    label={messages["unit_settings_engine_liters"]}
                    decimalPlaces={2}
                    max={10000}
                    min={0}
                    maxLength={12}
                    preventOnChange
                    onChange={(value) =>
                      setFormData({ ...formData, engine: value })
                    }
                    value={formData.engine}
                    placeholder={messages["unit_settings_engine_liters"]}
                    errorMessage={
                      messages[
                      "unit_settings_engine_liters_validation_message"
                      ]
                    }
                  />
                </div>
              </Fragment>
            );
          }

          const renderInputLabels = () => {
            return (
              <Box mt={3}>
                {
                  inputsCounter.map((i) => (
                    <Fragment key={i}>
                      <div className="unitForm form-item" style={{ width: "100%" }}>
                        {
                          //fInput?.labelId
                          //nInput?.id === fInput?.id
                        }
                        <Select
                          isClearable
                          id={"label" + i}
                          label={messages["unit_settings_input_label"] + " " + i}
                          placeholder={messages["select"]}
                          options={
                            inputLabels.filter(
                              (nInput) =>
                                formData.inputs?.filter(
                                  (fInput) =>
                                    nInput?.id === fInput?.labelId ||
                                    nInput?.id === fInput?.id
                                ).length == 0
                            ) || null
                          }
                          textField="title"
                          valueField="id"
                          onChange={(value) => {
                            let items = []
                            if (!value) {
                              items = formData.inputs.filter(x => x.position != i)
                            }
                            else {
                              items = formData.inputs.filter(x => x.position != i)
                              items.push({ ...value, labelId: value.id, position: i })
                            }

                            /*if (value && copyInputs.filter((element) => element?.title === value?.title).length > 0) {
                              NotificationManager.warning(
                                messages["unit_inputs_alert_message"],
                                messages["unit_inputs_alert_title"],
                                4000,
                                null,
                                null,
                                "warning",
                                null
                              );
                            } else {
                              if (value) {
                                copyInputs[i - 1] = value;
                              } else {
                                copyInputs[i - 1] = { title: value };
                              }//labelId*/

                            setFormData({
                              ...formData,
                              inputs: items,
                            });

                          }}
                          value={inputLabels.find(x => x.id == formData?.inputs?.find(x => x.position == i)?.labelId || x.id == formData?.inputs?.find(x => x.position == i)?.id)}
                        />
                      </div>
                    </Fragment>
                  ))
                }
              </Box>
            )
          }

          const renderOutputLabels = () => {
            return (
              <div className="form-item">
                {outputsCounter.map((i) => (
                  <Fragment key={i}>
                    <div className="unitForm" style={{ width: "100%" }}>
                      <div>
                        <Input
                          id={"title" + i}
                          type="text"
                          label={messages["unit_settings_output_label"] + " " + i}
                          maxLength={25}
                          value={
                            formData.outputs && formData.outputs[i - 1]
                              ? formData.outputs[i - 1]?.name
                              : ""
                          }
                          onChange={(value) => {
                            let copyOutputs = JSON.parse(
                              JSON.stringify(formData.outputs)
                            );
                            copyOutputs[i - 1] = {
                              ...copyOutputs[i - 1],
                              name: value,
                            };
                            setFormData({
                              ...formData,
                              outputs: copyOutputs,
                            });
                          }}
                          required={
                            formData.outputs &&
                            (formData.outputs[i - 1]?.name != null || formData.outputs[i - 1]?.type)
                          }
                          requiredMessage={messages["required_field"]}
                        />
                      </div>
                      <div>
                        <Select
                          id={"typeOutput" + i}
                          label={messages["unit_settings_type"]}
                          placeholder={
                            messages["select"]
                          }
                          options={selectOutputOptions || []}
                          textField="label"
                          valueField="type"
                          value={
                            formData.outputs && formData.outputs[i - 1]
                              ? (formData.outputs[i - 1]?.type?.type &&
                                formData.outputs[i - 1]?.type) ||
                              selectOutputOptions.filter(
                                (option) =>
                                  option.type ===
                                  formData.outputs[i - 1]?.type
                              )[0]
                              : {}
                          }
                          onChange={(value) => {
                            let copyOutputs = JSON.parse(
                              JSON.stringify(formData.outputs)
                            );
                            copyOutputs[i - 1] = {
                              ...copyOutputs[i - 1],
                              type: value,
                            };
                            setFormData({
                              ...formData,
                              outputs: copyOutputs,
                            });
                          }}
                          required={
                            formData.outputs &&
                            (formData.outputs[i - 1]?.name != null)
                          }
                          requiredMessage={messages["required_field"]}
                        />
                      </div>

                      {
                        i < outputsCounter?.length && <Divider style={{ marginTop: 15 }} />
                      }
                    </div>
                  </Fragment>
                ))}
              </div>
            )
          }

          const renderMpg = () => {
            return (
              <Box mt={1}>
                <div className="unitForm form-item" style={{ width: "100%" }}>
                  <Input
                    id="highway"
                    type="number"
                    label={messages["unit_settings_highway"]}
                    preventOnChange
                    max={200}
                    maxLength={6}
                    onChange={(value) =>
                      setFormData({ ...formData, mpgHighway: value })
                    }
                    value={formData.mpgHighway}
                    placeholder={messages["unit_settings_highway"]}
                    step="1"
                    pattern="[0-9]+"
                  />
                </div>

                <div className="unitForm form-item" style={{ width: "100%" }}>
                  <Input
                    id="city"
                    type="number"
                    label={messages["unit_settings_city"]}
                    preventOnChange
                    max={200}
                    maxLength={6}
                    onChange={(value) =>
                      setFormData({ ...formData, mpgCity: value })
                    }
                    value={formData.mpgCity}
                    placeholder={messages["unit_settings_city"]}
                    step="1"
                    pattern="[0-9]+"
                  />
                </div>
              </Box>
            )
          }

          const renderNotes = () => {
            return (
              <Box mt={1}>
                <div className="unitForm form-item">
                  <Input
                    id="notes"
                    label={
                      messages["unit_settings_notes"]
                    }
                    placeholder={
                      messages["unit_settings_notes"]
                    }
                    type="textarea"
                    maxLength={1000}
                    value={formData.notes}
                    onChange={(value) => {
                      setFormData({ ...formData, notes: value });
                    }}
                  />
                </div>
              </Box>
            )
          }

          const settingsPill = [
            {
              name: messages['unit_settings_unit'],
              id: settingsTab.UNIT,
              visible: true
            },
            {
              name: "ECU",
              id: settingsTab.DATA,
              visible: unit?.jbus || unit?.vbus
            },
            {
              name: 'VIN',
              id: settingsTab.VIN,
              visible: true
            },
            {
              name: messages["unit_settings_input_labels"],
              id: settingsTab.INPUT_LABELS,
              visible: supportedInputs > 0
            },
            {
              name: messages["unit_settings_output_labels"],
              id: settingsTab.OUTPUT_LABELS,
              visible: supportedOutputs > 0
            },
            {
              name: "MPG",
              id: settingsTab.MPG,
              visible: unit?.odometerEnable
            },
            {
              name: messages["unit_settings_notes"],
              id: settingsTab.NOTES,
              visible: true
            },
          ];

          return (
            <Fragment>
              <Box mt={3}>
                <Grid
                  container
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  spacing={1}
                >
                  {
                    settingsPill.map((filter, index) => {
                      if (filter.visible) {
                        let outlined = true;
                        if (filter.id === settingPillSelected) {
                          outlined = false;
                        }
                        return (
                          <Grid
                            item
                            xs
                            key={index}
                          >
                            <Pill
                              textField={'name'}
                              item={filter}
                              outlined={outlined}
                              onClick={() => {
                                setSettingPillSelected(filter.id)
                              }}
                              style={{ border: '0', width: '100%' }}
                              label="pill-label-text"
                              colorField="default"
                              colorSelection="005bf8"
                            />
                          </Grid>
                        )
                      }
                    })
                  }
                </Grid>
              </Box>

              <Grid container>
                <Grid item xs={12} sm={12}>
                  {renderPillSelected()}
                </Grid>
              </Grid>
            </Fragment>
          );
        }
      },
      {
        icon: "local_offer",
        label: messages['unit_settings_groups'],
        render: () => {
          return (
            <Fragment>
              <div
                className="input-normal-label"
                style={{
                  padding: '18px 0px 8px 0px'
                }}
              >
                {messages['unit_settings_belongs_tags']}
              </div>

              <div style={{ marginTop: 10, marginBottom: 10 }}>
                <TagsManage
                  mode="inline"
                  value={formData?.tags}
                  onChange={(values) => {
                    setFormData({ ...formData, tags: values });
                    setErrorMessage("");
                  }}
                />
              </div>

              {
                errorMessage &&
                <small className="select-error text-danger">{errorMessage}</small>
              }
            </Fragment>
          );
        }
      }
    ];

    return availableTabs;
  });

  useEffect(() => {
    dispatch(catalogLoad(["drivers", "locations", "inputLabels", "timeZones", "unitGroupsList", "vehicleStatus"]))
  }, []);

  useEffect(() => {
    if (unit && !unitId) {

      setUnitId(unit.id)
      setTitle(unit.label)
      setUpdateVehicleStatusIcon(filterIcon(unit) != null);

      let currentFormData = null;

      if (unit?.useEcuEngineHours && unit?.useEcuOdometer && unit?.useEcuSpeed && unit?.useEcuVin) {
        setEnableAll(true);
      } else {
        setEnableAll(false);
      }

      let alertContacts = [];
      if (unit?.alertContacts?.length) {
        alertContacts = [...unit?.alertContacts];
      }

      if (unit?.driverBehavior?.subscribers?.length) {
        unit?.driverBehavior?.subscribers?.forEach(subscriber => {
          if (!alertContacts?.find(c => c.id == subscriber?.contact?.id)) {
            alertContacts.push(subscriber?.contact);
          }
        });
      }

      if (unit?.towAlert?.subscribers?.length) {
        unit?.towAlert?.subscribers?.forEach(subscriber => {
          if (!alertContacts?.find(c => c.id == subscriber?.contact?.id)) {
            alertContacts.push(subscriber?.contact);
          }
        });
      }

      if (unit?.motionAlert?.subscribers?.length) {
        unit?.motionAlert?.subscribers?.forEach(subscriber => {
          if (!alertContacts?.find(c => c.id == subscriber?.contact?.id)) {
            alertContacts.push(subscriber?.contact);
          }
        });
      }

      if (unit?.consistentSpeedAlert?.subscribers?.length) {
        unit?.consistentSpeedAlert?.subscribers?.forEach(subscriber => {
          if (!alertContacts?.find(c => c.id == subscriber?.contact?.id)) {
            alertContacts.push(subscriber?.contact);
          }
        });
      }

      if (alertContacts?.length && alertContacts?.find(c => c?.id === -1)) {
        alertContacts = [...contacts];
      }

      const momentFormat = user?.dateformat ? `${GetFormatForMoment(user.dateformat)} h:mm a` : null;
      currentFormData = {
        ...formData,

        id: unit.id,
        type: unit.type,
        hid: unit.hid,
        label: unit.label || "",

        useEcuOdometer: unit.useEcuOdometer || false,
        engineOdometer: unit.lastReading?.engineOdometer && Number(unit.lastReading?.engineOdometer).toFixed(2) || 0,
        ecuEngineOdometer: unit.lastReading?.ecuEngineOdometer && Number(unit.lastReading?.ecuEngineOdometer).toFixed(2) || 0,
        offsetEcuEngineOdometer: unit.offsetEcuEngineOdometer && Number(unit.offsetEcuEngineOdometer).toFixed(2) || 0,
        offsetEngineOdometer: unit.offsetEngineOdometer && Number(unit.offsetEngineOdometer).toFixed(2) || 0,
        useEcuEngineHours: unit.useEcuEngineHours || false,
        engineHours: unit.lastReading?.engineHours && Number(unit.lastReading?.engineHours).toFixed(2) || 0,
        ecuEngineHours: unit.lastReading?.ecuEngineHours && Number(unit.lastReading?.ecuEngineHours).toFixed(2) || 0,
        offsetEcuEngineHours: unit.offsetEcuEngineHours && Number(unit?.offsetEcuEngineHours).toFixed(2) || 0,
        offsetEngineHours: unit.offsetEngineHours && Number(unit.offsetEngineHours).toFixed(2) || 0,
        useEcuSpeed: unit.useEcuSpeed || false,
        speed: unit.lastReading?.speed && Number(unit.lastReading?.speed).toFixed(2) || 0,
        ecuSpeed: unit.lastReading?.ecuSpeed && Number(unit.lastReading?.ecuSpeed).toFixed(2) || 0,
        useEcuVin: unit.useEcuVin || false,
        vin: unit.asset?.vin || unit.lastReading?.vin || "",
        ecuVin: unit.lastReading?.ecuVin || "",
        unitGroups: unit.unitGroups || [],
        driverId: unit.driverId,
        defaultDriverId: HasPermission(user, "defDrvr", [290]) ? unit?.defaultDriverId : null,
        locationId: unit.locationId,
        timeZoneId: unit.timeZoneId,
        offsetEngineHoursUpdatedOnLabel: getFormattedDateByTimeZone(unit.offsetEngineHoursUpdatedOn, user?.timeZone, messages, momentFormat, false, locale),
        offsetEngineOdometerUpdatedOnLabel: getFormattedDateByTimeZone(unit.offsetEngineOdometerUpdatedOn, user?.timeZone, messages, momentFormat, false, locale),

        year: unit.asset?.year,
        make: unit.asset?.make,
        model: unit.asset?.model,
        engine: unit.asset?.engine,
        licensePlate: unit.asset?.licensePlate,

        mpgHighway: unit.asset?.mpgHighway && Number(unit.asset?.mpgHighway) || 0,
        mpgCity: unit.asset?.mpgCity && Number(unit.asset?.mpgCity) || 0,

        supportedOutputs: unit.supportedOutputs || 0,
        supportedInputs: unit.supportedInputs || 0,

        inputs: unit.inputs || [],
        outputs: unit.outputs || [],

        notes: unit.notes || "",

        jbus: unit.jbus,
        vbus: unit.vbus,

        vehicleStatusId: unit.vehicleStatusId,

        tags: unit?.tags || [],

        //Alert
        lowBattery: unit.lowBattery ? true : false,
        noGps: unit.noGps ? true : false,
        noGpsTimes: unit.noGpsTimes || 0,
        noGpsDays: unit.noGpsDays || 0,

        consistentSpeed: unit.consistentSpeed || 0,
        alertContacts: alertContacts,
        //Driver Behavior
        driverBehaviorAlert: {},
        //Tow
        towAlert: {
          tow: unit?.towAlert ? true : false
        },
        //Motion
        motionAlert: {
          motion: unit?.motionAlert ? true : false
        },
        //Consistent Speed
        consistentSpeedAlert: {
          consistentSpeed: unit?.consistentSpeedAlert ? true : false
        },
      };

      if (unit?.driverBehaviorAlert && unit?.driverBehaviorAlert?.configJSON) {
        let config = JSON.parse(unit?.driverBehaviorAlert?.configJSON);
        if (config) {
          currentFormData.driverBehaviorAlert = {
            rapidAcceleration: config?.driverBehaviorEvents?.includes("RapidAcceleration"),
            hardBrake: config?.driverBehaviorEvents?.includes("HardStop"),
            suddenStop: config?.driverBehaviorEvents?.includes("SuddenStop"),
            hardTurn: config?.driverBehaviorEvents?.includes("HardTurn"),
          };
        }
      }

      if (unit.manufacturerId === 2) {
        currentFormData = {
          ...currentFormData,
          manufacturerId: unit.manufacturerId,
          consistentSpeed: unit.consistentSpeed,
        };
      }

      if (currentFormData.year === 0) {
        currentFormData.year = null;
      }

      if (!currentFormData.jbus && !currentFormData.vbus) {
        currentFormData.useEcuOdometer = false;
        currentFormData.useEcuEngineHours = false;
        currentFormData.useEcuSpeed = false;
      }


      if (currentFormData.useEcuOdometer) {
        currentFormData.dashOdometer =
          Number(
            getValueFloat(currentFormData.offsetEcuEngineOdometer) +
            getValueFloat(currentFormData.ecuEngineOdometer)
          ).toFixed(2) || 0;
      } else {
        currentFormData.dashOdometer =
          Number(
            getValueFloat(currentFormData.offsetEngineOdometer) +
            getValueFloat(currentFormData.engineOdometer)
          ).toFixed(2) || 0;
      }

      currentFormData.dashOdometer =
        currentFormData.dashOdometer > 100000000
          ? 100000000
          : currentFormData.dashOdometer;


      if (user.isKph) {
        currentFormData.dashOdometer = convertToKPH(currentFormData.dashOdometer)
        currentFormData.ecuEngineOdometer = convertToKPH(currentFormData.ecuEngineOdometer)
        currentFormData.offsetEcuEngineOdometer = convertToKPH(currentFormData.offsetEcuEngineOdometer)
        currentFormData.engineOdometer = convertToKPH(currentFormData.engineOdometer)
        currentFormData.offsetEngineOdometer = convertToKPH(currentFormData.offsetEngineOdometer)
      }

      if (currentFormData.useEcuEngineHours) {
        currentFormData.dashEngineHours =
          Number(
            getValueFloat(currentFormData.offsetEcuEngineHours) +
            getValueFloat(currentFormData.ecuEngineHours)
          ).toFixed(2) || 0;
      } else {
        currentFormData.dashEngineHours =
          Number(
            getValueFloat(currentFormData.offsetEngineHours) +
            getValueFloat(currentFormData.engineHours)
          ).toFixed(2) || 0;
      }

      currentFormData.dashEngineHours =
        currentFormData.dashEngineHours > 1000000
          ? 1000000
          : currentFormData.dashEngineHours;

      setFormData(currentFormData);

      setSupportedInputs(unit.supportedInputs || 0);
      setSupportedOutputs(unit.supportedOutputs || 0);

      let sInputs = [];

      let sOutputs = [];

      if (unit.supportedInputs > 0) {
        for (let i = 1; i <= unit.supportedInputs; i++) {
          sInputs.push(i);
        }
      }

      if (unit.supportedOutputs > 0) {
        for (let i = 0; i < unit.supportedOutputs; i++) {
          sOutputs.push(i + 1);
        }
      }

      setInputsCounter(sInputs);

      setOutputsCounter(sOutputs);
    }

  }, [unit])

  useEffect(() => {
    if (resultUpdate) {
      if (resultUpdate.result) {
        NotificationManager.success(
          messages["unit_validation_succes_message"],
          messages["unit_validation_success_title"],
          3000,
          null,
          null,
          "warning",
          null
        );

        let driver = drivers?.find(d => d?.driverId == formData?.driverId);
        dispatch(catalogProcessItem("Unit", {
          id: formData.id,
          CUnits_id: formData.id,
          label: formData.label,
          Label: formData.label,
          useEcuOdometer: formData.useEcuOdometer,
          offsetEcuEngineOdometer: formData.offsetEcuEngineOdometer,
          offsetEngineOdometer: formData.offsetEngineOdometer,
          useEcuEngineHours: formData.useEcuEngineHours,
          offsetEcuEngineHours: formData.offsetEcuEngineHours,
          offsetEngineHours: formData.offsetEngineHours,
          useEcuVin: formData.useEcuVin,
          tags: formData.tags,
          driverId: formData?.driverId,
          driverName: driver?.name || null,
          ...(HasPermission(user, "defDrvr", [290]) ? { defaultDriverId: formData?.defaultDriverId } : {})
        }));

        //Remove the driver from the others units
        let newUnits = units.filter(x => x.id != formData.id).map(newUnit => {
          let driverId = newUnit?.driverId;
          let driverName = newUnit?.driverName;

          if (newUnit?.id != formData.id && newUnit?.driverId == formData?.driverId) {
            driverId = 0;
            driverName = "";
          }

          return {
            ...newUnit,
            driverId,
            driverName
          };
        });

        dispatch(catalogProcessItems("Unit", newUnits));

        dispatch(catalogProcessItem("Asset", {
          unit_id: formData.id,
          id: formData.id,
          vin: formData.vin,
          ecuVin: formData.ecuVin,
          make: formData.make,
          model: formData.model,
          year: formData.year,
          engine: formData.engine,
          mpgHighway: formData.mpgHighway,
          mpgCity: formData.mpgCity,
          licensePlate: formData.licensePlate
        }))

        dispatch(catalogProcessItem("LastReading", {
          deviceId: formData.id,
          engineOdometer: formData.engineOdometer,
          ecuEngineOdometer: formData.ecuEngineOdometer,
          engineHours: formData.engineHours,
          ecuEngineHours: formData.ecuEngineHours,
          ecuVin: formData.ecuVin,
          vehicleStatusId: formData.vehicleStatusId,
          vin: formData.vin,
        }))

        dispatch(clearRedux("SETTINGS_DRIVER", { refresh: true }))
        setTimeout(() => handleClose(), 400);

      } else {
        NotificationManager.error(
          messages[GetApiErrorLan(resultUpdate.error)] || messages["errorMessage"],
          messages["error"],
          3000,
          null,
          null,
          "error",
          null
        );
      }

      dispatch(clearRedux("MODAL_EDIT_UNIT", { resultUpdate: null }))
    }
  }, [resultUpdate]);

  useEffect(() => {
    if (asset) {
      if (asset?.error) {
        NotificationManager.error(
          messages["unit_settings_decode_vin_error"],
          messages["error"],
          3000,
          null,
          null,
          "error",
          null
        );
      } else {
        setFormData({
          ...formData,
          year: asset?.ModelYear,
          make: asset?.Make,
          model: asset?.Model,
          engine: asset?.DisplacementL
        });
      }

      dispatch(clearRedux("MODAL_EDIT_UNIT", { asset: null }));
    }
  }, [asset]);

  return (
    <>
      {openModalTroubleshootDevice && <ModalTroubleshootDevice />}
      <Modal
        title={
          (title && title.length > 28 ? title.substring(0, 25) + "..." : title) || ''
        }
        position="right"
        size="small"
        open={isOpen}
        handleClose={handleClose}
        loading={loading || vehicleInformationLoading}
      >
        <ModalBody
          style={{
            height: "-webkit-fill-available"
          }}
        >
          <div style={{ padding: '0px 25px 120px 25px' }}>
            <Form ref={frmCompleteService} onValidSubmit={onSubmit}>
              {
                unit &&
                <Tabs
                  tab={tab}
                  type="icon-indicator"
                  border
                  onChange={(newTab) => {
                    setTab(newTab);
                    setErrorMessage("");
                  }}
                >
                  {getTabs().map((tab, index) => {
                    return (
                      <TabContainer key={index} iconIndicator={tab.icon} label={tab.label} index={index} style={{ padding: 0 }}>
                        {tab.render()}
                      </TabContainer>
                    )
                  })}
                </Tabs>
              }
            </Form>
          </div>
        </ModalBody>
        <ModalFooter>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Button
                className="btn-cancel"
                onClick={() => {
                  handleClose();
                }}
              >
                {messages["cancel"]}
              </Button>
              {
                HasPermission(user, "", [13]) ?
                  <Button
                    className="btn-blue"
                    onClick={(e) => {
                      if (!props.loadingGetUnit && !props.loadingUpdateUnit) {
                        e.preventDefault();
                        const validation = frmCompleteService.current.submit();
                        if (!validation) {
                          NotificationManager.info(
                            messages["unit_validation_alert_message"],
                            messages["unit_validation_alert_title"],
                            5000,
                            null,
                            null,
                            "info",
                            null
                          );
                        }
                      }
                    }}
                  >
                    {messages["save"]}
                  </Button> : ''
              }
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {
                HasPermission(user, "trbusr", [262]) &&
                <div style={{ display: 'flex', marginRight: 10 }}>
                  <Icon>troubleshoot</Icon>
                  <a
                    onClick={() => {
                      dispatch(openTroubleshootDeviceModal({ open: true, unit: unit }));
                      handleClose();
                    }} style={{ textDecoration: 'underline', cursor: 'pointer' }}>{messages['unit_settings_start_troubleshooting']}</a>
                </div>
              }
            </div>
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default EditUnitSettings;
